import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { debounce } from "lodash";
import performRequest from "network/perform-request";
import { leagueCustomerRosterMembersRoute, leagueCustomerTeamPlayersRoute } from "network/api-routes";
import { parseLeaguePaginatedResponse } from "components/pages/Gestion/gestion-pagination";
import {
  addCheckedToSelection,
  buildAssignPayload,
  conflictMemberIds,
  isSelectableMember,
  memberFullName,
  memberSecondaryLabel,
  memberTeamStatus,
} from "components/pages/Gestion/team-player-picker-helpers";

const PAGE_SIZE = 25;
const PANEL_HEIGHT = { xs: 260, sm: 380 };

// Two-panel picker modelled on the lineup "Agregar jugadores" dialog: the customer's whole player list on the
// left (greyed out when already on a team), the players to add on the right, saved in a single request.
const GestionTeamPlayerPicker = ({ open, onClose, customerId, team, user, onAssigned }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const teamId = team?.id;

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [members, setMembers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);
  const [selection, setSelection] = useState([]);
  const [saving, setSaving] = useState(false);

  const requestIdRef = useRef(0);
  const userRef = useRef(user);
  userRef.current = user;

  const load = useCallback(
    (name, pageIndex) => {
      if (!customerId || !userRef.current) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      performRequest(
        "GET",
        leagueCustomerRosterMembersRoute(customerId),
        { name: name || undefined, with_teams: 1, page: pageIndex, page_size: PAGE_SIZE },
        true,
        userRef.current,
      )
        .then((response) => {
          if (requestId !== requestIdRef.current) return;
          const parsed = parseLeaguePaginatedResponse(response.data);
          setMembers(parsed.results);
          setPage(parsed.page);
          setTotalPages(parsed.totalPages);
          setTotalResults(parsed.totalResults);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setMembers([]);
          setTotalResults(0);
          setTotalPages(1);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    },
    [customerId],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((name) => {
        setCheckedIds([]);
        load(name, 0);
      }, 400),
    [load],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setCheckedIds([]);
    setSelection([]);
    setPage(0);
    load("", 0);
  }, [open, load]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    debouncedSearch(event.target.value.trim());
  };

  const handlePageChange = (_event, value) => {
    debouncedSearch.cancel();
    setCheckedIds([]);
    load(search.trim(), value - 1);
  };

  const selectedIds = useMemo(() => new Set(selection.map((entry) => String(entry.member.id))), [selection]);
  // Free members of this page that are not in the selection yet: the only rows that can be ticked.
  const pickable = members.filter((member) => isSelectableMember(member, teamId) && !selectedIds.has(String(member.id)));
  const allChecked = pickable.length > 0 && pickable.every((member) => checkedIds.includes(member.id));

  const toggleChecked = (id) =>
    setCheckedIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  const toggleAll = () => setCheckedIds(allChecked ? [] : pickable.map((member) => member.id));

  const moveChecked = () => {
    setSelection((current) => addCheckedToSelection(current, members, checkedIds, teamId));
    setCheckedIds([]);
  };

  const removeFromSelection = (id) => setSelection((current) => current.filter((entry) => entry.member.id !== id));

  const setShirt = (id, value) =>
    setSelection((current) => current.map((entry) => (entry.member.id === id ? { ...entry, shirt_number: value } : entry)));

  const handleSave = () => {
    if (!customerId || !teamId || selection.length === 0) return;
    setSaving(true);
    performRequest("POST", leagueCustomerTeamPlayersRoute(customerId, teamId), buildAssignPayload(selection), true, user)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.teamPicker.assigned" }, { count: selection.length }), {
          variant: "success",
        });
        onAssigned?.();
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
          variant: "error",
        });
        // Someone else put a player on another team meanwhile: drop them and refresh the greyed-out rows.
        const conflicts = conflictMemberIds(error);
        if (conflicts.length > 0) {
          setSelection((current) => current.filter((entry) => !conflicts.includes(String(entry.member.id))));
          load(search.trim(), page);
        }
      })
      .finally(() => setSaving(false));
  };

  const renderMember = (member) => {
    const status = memberTeamStatus(member, teamId);
    const inSelection = selectedIds.has(String(member.id));
    const disabled = status.kind !== "free" || inSelection;
    const secondary = memberSecondaryLabel(member);
    let note = null;
    if (status.kind === "this") {
      note = intl.formatMessage({ id: "gestion.teamPicker.alreadyInTeam" });
    } else if (status.kind === "other") {
      note = intl.formatMessage({ id: "gestion.teamPicker.onTeam" }, { team: status.teamName });
    } else if (inSelection) {
      note = intl.formatMessage({ id: "gestion.teamPicker.inSelection" });
    }
    const tooltip =
      status.kind === "other"
        ? intl.formatMessage({ id: "gestion.teamPicker.removeFirstHint" }, { team: status.teamName })
        : "";

    return (
      <ListItem key={member.id} disablePadding data-testid={`picker-member-${member.id}`}>
        <Tooltip title={tooltip} placement="top-start">
          {/* The span keeps the tooltip alive on a disabled button. */}
          <Box component="span" sx={{ width: "100%" }}>
            <ListItemButton disabled={disabled} onClick={() => toggleChecked(member.id)} sx={{ "&.Mui-disabled": { opacity: 0.55 } }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  size="small"
                  checked={checkedIds.includes(member.id)}
                  disabled={disabled}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={memberFullName(member)} secondary={secondary || null} />
              {note && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: "nowrap" }}>
                  {note}
                </Typography>
              )}
            </ListItemButton>
          </Box>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ sx: { maxHeight: "90vh", display: "flex", flexDirection: "column" } }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>
        {intl.formatMessage({ id: "gestion.teamPicker.title" }, { team: team?.name || "" })}
      </DialogTitle>
      <DialogContent sx={{ overflow: "auto" }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            label={intl.formatMessage({ id: "gestion.teamPicker.searchLabel" })}
            value={search}
            onChange={handleSearchChange}
            inputProps={{ "data-testid": "team-picker-search" }}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
            <Paper variant="outlined" sx={{ flex: 1, height: PANEL_HEIGHT, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
                <Checkbox
                  size="small"
                  checked={allChecked}
                  indeterminate={checkedIds.length > 0 && !allChecked}
                  onChange={toggleAll}
                  disabled={pickable.length === 0}
                  inputProps={{ "aria-label": intl.formatMessage({ id: "gestion.teamPicker.selectAll" }) }}
                />
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  {intl.formatMessage({ id: "gestion.teamPicker.availablePlayers" })}
                </Typography>
                {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
              </Stack>
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {members.length === 0 && !loading ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {intl.formatMessage({ id: "gestion.teamPicker.emptyAvailable" })}
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {members.map(renderMember)}
                  </List>
                )}
              </Box>
              {totalResults > PAGE_SIZE && (
                <Stack direction="row" justifyContent="center" sx={{ py: 0.5, borderTop: 1, borderColor: "divider", flexShrink: 0 }}>
                  <Pagination size="small" count={Math.max(totalPages, 1)} page={page + 1} onChange={handlePageChange} disabled={loading} />
                </Stack>
              )}
            </Paper>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Tooltip title={intl.formatMessage({ id: "gestion.teamPicker.moveToSelection" })}>
                <span>
                  <Button
                    variant="contained"
                    onClick={moveChecked}
                    disabled={checkedIds.length === 0}
                    aria-label={intl.formatMessage({ id: "gestion.teamPicker.moveToSelection" })}
                  >
                    <ChevronRightIcon />
                  </Button>
                </span>
              </Tooltip>
            </Box>

            <Paper variant="outlined" sx={{ flex: 1, height: PANEL_HEIGHT, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Typography variant="subtitle2" sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
                {intl.formatMessage({ id: "gestion.teamPicker.selectedPlayers" }, { count: selection.length })}
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {selection.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {intl.formatMessage({ id: "gestion.teamPicker.emptySelected" })}
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {selection.map(({ member, shirt_number: shirt }) => (
                      <ListItem key={member.id} data-testid={`picker-selected-${member.id}`}>
                        <ListItemText primary={memberFullName(member)} secondary={memberSecondaryLabel(member) || null} sx={{ pr: 1 }} />
                        <TextField
                          size="small"
                          type="number"
                          value={shirt}
                          onChange={(event) => setShirt(member.id, event.target.value)}
                          placeholder={intl.formatMessage({ id: "gestion.teamDetail.shirtNumber" })}
                          inputProps={{
                            min: 0,
                            max: 999,
                            "aria-label": intl.formatMessage({ id: "gestion.teamDetail.shirtNumber" }),
                            "data-testid": `picker-shirt-${member.id}`,
                          }}
                          sx={{ width: 84, mr: 1 }}
                        />
                        <Tooltip title={intl.formatMessage({ id: "gestion.teamPicker.removeFromSelection" })}>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => removeFromSelection(member.id)}
                            aria-label={intl.formatMessage({ id: "gestion.teamPicker.removeFromSelection" })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Paper>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {intl.formatMessage({ id: "Cancelar" })}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || selection.length === 0}>
          {intl.formatMessage({ id: saving ? "Guardando..." : "gestion.teamPicker.save" }, { count: selection.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GestionTeamPlayerPicker;
